import RFB from '@novnc/novnc'
import { useCallback, useEffect, useRef, useState } from 'react'
import { getGatewayWsUrl } from '../../utils/deviceGateway'
import { getCachedAuthToken } from '../LiveView/api/getCachedAuthToken'

export type VncConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'timeout'
export type VncMode = 'view' | 'interactive'

// Cap a session like the camera live view (gateway enforces the same limit): a forgotten tab
// disconnects instead of holding the device connection open indefinitely.
const SESSION_TIMEOUT_MS = 45 * 60 * 1000

// The gateway closes the WS with a specific code/reason when it rejects a session (auth, missing
// credentials, device offline, device-side 401). noVNC's `disconnect` event doesn't expose these,
// so we capture the raw close event and translate it into a user-facing message.
const describeCloseError = (code: number | undefined, reason: string | undefined): string => {
    const r = (reason ?? '').toLowerCase()
    if (r.includes('credentials not configured')) return 'Live view is not configured for this device yet.'
    if (r.includes('device rejected') || r.includes('401'))
        return 'The device rejected the connection (authentication failed).'
    if (r.includes('unreachable') || r.includes('offline')) return 'The device is offline or unreachable.'
    if (r.includes('unauthorized') || r.includes('token'))
        return 'Your session is no longer authorized. Refresh and sign in again.'
    if (reason) return reason
    if (code === 1006) return 'Could not reach the live view service. Check your connection and try again.'
    return 'Connection lost. The device may be offline or unreachable.'
}

interface UseVncViewerParams {
    /** Device VPN IP the gateway proxies the VNC stream from. */
    ip: string | null | undefined
    /** 'view' is read-only; 'interactive' forwards keyboard/mouse (terminals only). */
    mode: VncMode
    /** Only hold a connection while the live-view tab is actually visible. */
    enabled: boolean
    /** true: scale the framebuffer to fit the container; false: render native size (scrollable). */
    scaleToFit: boolean
}

/**
 * Manages a noVNC connection to a device's terminal/screen via the device gateway.
 * The gateway (/novnc) authenticates to the device with HTTP Basic and bridges the raw VNC stream.
 */
export const useVncViewer = ({ ip, mode, enabled, scaleToFit }: UseVncViewerParams) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [status, setStatus] = useState<VncConnectionStatus>('idle')
    const [error, setError] = useState<string | null>(null)
    const [reconnectNonce, setReconnectNonce] = useState(0)

    const reconnect = useCallback(() => setReconnectNonce((nonce) => nonce + 1), [])

    useEffect(() => {
        const container = containerRef.current
        if (!enabled || !ip || !container) {
            setStatus('idle')
            return
        }

        const token = getCachedAuthToken()
        if (!token) {
            setStatus('error')
            setError('Not authenticated. Please refresh and sign in again.')
            return
        }

        setStatus('connecting')
        setError(null)

        const url = getGatewayWsUrl('/novnc')

        // Captured from the socket's close event so onDisconnect can report the gateway's real cause.
        let closeCode: number | undefined
        let closeReason: string | undefined

        // Auth + target go in the first WS message (like /livestream) so the JWT stays out of the
        // URL and gateway access logs. We hand RFB the still-connecting socket and send our frame on
        // open; RFB waits for the server's ProtocolVersion before sending, so ours is always first.
        let rfb: RFB
        try {
            const socket = new WebSocket(url, ['binary'])
            socket.addEventListener(
                'close',
                (event) => {
                    closeCode = event.code
                    closeReason = event.reason
                },
                { once: true }
            )
            socket.addEventListener(
                'open',
                () => {
                    try {
                        socket.send(JSON.stringify({ authToken: token, host: ip, mode }))
                    } catch {
                        setStatus('error')
                        setError('Failed to start the live view.')
                    }
                },
                { once: true }
            )
            rfb = new RFB(container, socket)
        } catch {
            setStatus('error')
            setError('Failed to start the live view.')
            return
        }

        // Only forward keyboard/mouse to the device in interactive mode; 'view' stays read-only.
        rfb.viewOnly = mode === 'view'
        rfb.scaleViewport = scaleToFit
        rfb.background = '#000'

        // Time the session from the moment it actually connects (like the live-view stream cap).
        let sessionTimedOut = false
        let sessionTimer: ReturnType<typeof setTimeout> | undefined

        const onConnect = () => {
            setStatus('connected')
            setError(null)
            sessionTimer = setTimeout(() => {
                sessionTimedOut = true
                setStatus('timeout')
                setError('Session ended after 45 min — reconnect to continue.')
                try {
                    rfb.disconnect()
                } catch {
                    /* already disconnected */
                }
            }, SESSION_TIMEOUT_MS)
        }
        const onDisconnect = (event: Event) => {
            // The session-timeout path already set the status/message; don't overwrite it with the
            // generic clean-disconnect handling triggered by our own rfb.disconnect() call.
            if (sessionTimedOut) return
            const clean = (event as CustomEvent<{ clean: boolean }>).detail?.clean
            if (clean) {
                setStatus('idle')
            } else {
                setStatus('error')
                setError(describeCloseError(closeCode, closeReason))
            }
        }
        const onSecurityFailure = (event: Event) => {
            const reason = (event as CustomEvent<{ reason?: string }>).detail?.reason
            setStatus('error')
            setError(reason || 'Authentication to the device failed.')
        }

        rfb.addEventListener('connect', onConnect)
        rfb.addEventListener('disconnect', onDisconnect)
        rfb.addEventListener('securityfailure', onSecurityFailure)

        return () => {
            if (sessionTimer) clearTimeout(sessionTimer)
            rfb.removeEventListener('connect', onConnect)
            rfb.removeEventListener('disconnect', onDisconnect)
            rfb.removeEventListener('securityfailure', onSecurityFailure)
            try {
                rfb.disconnect()
            } catch {
                /* already disconnected */
            }
        }
    }, [ip, mode, enabled, scaleToFit, reconnectNonce])

    return { containerRef, status, error, reconnect }
}
